// Klinik geneli tıbbi kayıt listeleri (Tedaviler, Reçeteler, Tetkikler, Diyetler)
// için ortak tablo parçaları. Hücre dili Hastalar listesiyle aynıdır.
// Kurallar: docs/design-principles.md §3

import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Skeleton,
  TableCell,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import { COLORS, TYPO } from "../../../styles/uiConstants";
import {
  ageOf,
  avatarTone,
  fullDateTime,
  fullName,
  initialsOf,
  maskTc,
  relativePast,
} from "../../../pages/Secretary/PatientsPage/patientListFormat";
import { readPersistedFilters, writePersistedFilters } from "../../../hooks/usePersistedFilters";

export const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

// İlk/son hücre başlık şeridi hizasında
export const EDGE_L = { pl: { xs: 2, md: 4 } };
export const EDGE_R = { pr: { xs: 2, md: 4 } };

export const headSx = {
  fontWeight: 600,
  fontSize: TYPO.caption,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: COLORS.slate500,
  bgcolor: COLORS.white,
  borderBottom: `1px solid ${COLORS.slate200}`,
  py: 1.25,
  whiteSpace: "nowrap",
};

export const cellSx = { py: 1.25, borderBottom: `1px solid ${COLORS.slate100}` };
export const mainText = { fontSize: TYPO.body, fontWeight: 500, color: COLORS.slate800, lineHeight: 1.35 };
export const subText = { fontSize: TYPO.caption, color: COLORS.slate500, lineHeight: 1.35 };

/** Boş hücre: soluk "—". */
export const Dash = () => <Typography sx={{ ...subText, color: COLORS.slate300 }}>—</Typography>;

/** Sıralanabilir başlık hücresi. */
export function SortHead({ field, label, sort, dir, onSort, defaultDir = "asc", sx }) {
  const active = sort === field;
  return (
    <TableCell sx={{ ...headSx, ...sx }} sortDirection={active ? dir : false}>
      <TableSortLabel
        active={active}
        direction={active ? dir : defaultDir}
        onClick={() => onSort(field)}
        sx={{ "&.Mui-active": { color: COLORS.slate800 }, "& .MuiTableSortLabel-icon": { fontSize: 16 } }}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );
}

export const PlainHead = ({ children, sx }) => <TableCell sx={{ ...headSx, ...sx }}>{children}</TableCell>;

/** Tıklanabilir satır: hover yalnız zemin tonu, kalkma yok. */
export function ClickableRow({ onClick, palette, children }) {
  return (
    <TableRow
      hover
      onClick={onClick}
      sx={{ cursor: "pointer", "&:hover": { bgcolor: `${alpha(palette.primary, 0.03)} !important` } }}
    >
      {children}
    </TableRow>
  );
}

/** Kişi hücresi: avatar (baş harfler, isme göre sabit ton) + ad + "45 yaş · Kadın · TC 123******14". */
export function PatientCell({ patient, sx = EDGE_L }) {
  const tone = avatarTone(patient);
  const age = ageOf(patient?.birthDay);
  const meta = [
    age != null ? `${age} yaş` : null,
    patient?.gender,
    patient?.tcNo ? `TC ${maskTc(patient.tcNo)}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <TableCell sx={{ ...cellSx, ...sx }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
        <Avatar sx={{ width: 34, height: 34, fontSize: 12.5, fontWeight: 600, bgcolor: alpha(tone, 0.14), color: tone }}>
          {initialsOf(patient)}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ ...mainText, fontWeight: 600 }} noWrap>
            {fullName(patient)}
          </Typography>
          {meta ? (
            <Typography sx={subText} noWrap>
              {meta}
            </Typography>
          ) : null}
        </Box>
      </Box>
    </TableCell>
  );
}

/** Ana metin + alt satır; uzunsa tooltip'te tamamı. */
export function TextCell({ main, sub, tooltip, sx }) {
  return (
    <TableCell sx={{ ...cellSx, ...sx }}>
      {main ? (
        <Box sx={{ minWidth: 0, pr: 2 }}>
          <Tooltip title={tooltip || ""} arrow placement="top" disableHoverListener={!tooltip}>
            <Typography sx={mainText} noWrap>
              {main}
            </Typography>
          </Tooltip>
          {sub ? (
            <Typography sx={subText} noWrap>
              {sub}
            </Typography>
          ) : null}
        </Box>
      ) : (
        <Dash />
      )}
    </TableCell>
  );
}

/** Geçmiş tarih göreli ("3 gün önce"); tooltip'te tam tarih ve saat. */
export function DateCell({ value, sx }) {
  const rel = relativePast(value);
  return (
    <TableCell sx={{ ...cellSx, ...sx }}>
      {rel ? (
        <Tooltip title={fullDateTime(value)} arrow placement="top">
          <Typography sx={{ ...mainText, display: "inline-block", whiteSpace: "nowrap" }}>{rel}</Typography>
        </Tooltip>
      ) : (
        <Dash />
      )}
    </TableCell>
  );
}

/**
 * Durum rozeti. tone: "success" | "warning" | "error" | "neutral" | "primary".
 * Renk durumu anlatır; metin her zaman yazılı (renge bırakılmaz).
 */
export function StatusPill({ label, tone = "neutral", palette }) {
  const TONES = {
    success: { bgcolor: COLORS.successLight, color: COLORS.successDarker },
    warning: { bgcolor: COLORS.warningLight, color: COLORS.warningDarker },
    error: { bgcolor: COLORS.errorLight, color: COLORS.errorDark },
    neutral: { bgcolor: COLORS.slate100, color: COLORS.slate700 },
    primary: { bgcolor: alpha(palette?.primary || COLORS.slate700, 0.1), color: palette?.primary || COLORS.slate700 },
  };
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        px: 1,
        py: 0.25,
        borderRadius: 1.5,
        fontSize: TYPO.label,
        fontWeight: 600,
        whiteSpace: "nowrap",
        ...(TONES[tone] || TONES.neutral),
      }}
    >
      {label}
    </Box>
  );
}

/** Drawer içi salt-okunur bilgi listesi: etiket solda, değer sağda, aralarda ince çizgi. */
export function DetailList({ items }) {
  return (
    <Box sx={{ px: 2, py: 1 }}>
      {items.filter(Boolean).map((it) => (
        <Box
          key={it.label}
          sx={{
            display: "flex",
            alignItems: "baseline",
            gap: 2,
            py: 1.5,
            borderBottom: `1px solid ${COLORS.slate100}`,
          }}
        >
          <Typography sx={{ fontSize: TYPO.label, color: COLORS.slate500, width: 140, flexShrink: 0 }}>
            {it.label}
          </Typography>
          <Box sx={{ ...mainText, flex: 1, minWidth: 0, wordBreak: "break-word", ...(it.mono ? { fontFamily: "'SF Mono', 'Fira Code', monospace" } : {}) }}>
            {it.value ?? <Dash />}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

/** Yükleme iskeleti satırları. */
export function SkeletonRows({ rows, cols }) {
  return [...Array(rows)].map((_, i) => (
    <TableRow key={`sk-${i}`}>
      {Array.from({ length: cols }).map((__, c) => (
        <TableCell
          key={c}
          sx={{ ...cellSx, ...(c === 0 ? EDGE_L : {}), ...(c === cols - 1 ? EDGE_R : {}) }}
        >
          <Skeleton variant="text" />
        </TableCell>
      ))}
    </TableRow>
  ));
}

/** DataTableCard boşken tabloyu ayakta tutan dolgu satırı. */
export const FillerRow = ({ cols }) => (
  <TableRow>
    <TableCell colSpan={cols} sx={{ height: 1, py: 0, border: 0 }} />
  </TableRow>
);

export const patientNameOf = (u) => fullName(u);

const collator = new Intl.Collator("tr", { sensitivity: "base" });
const timeOf = (v) => (v ? new Date(v).getTime() || 0 : 0);

/** Sık kullanılan sıralayıcılar. */
export const SORTERS = {
  name: (getPatient) => (a, b) => collator.compare(fullName(getPatient(a)), fullName(getPatient(b))),
  date: (getDate) => (a, b) => timeOf(getDate(a)) - timeOf(getDate(b)),
};

/**
 * İstemci tarafı liste durumu: arama + hazır filtre (segment) + sıralama + sayfalama.
 * Kayıtlar klinik başına birkaç bini geçmediği için bellekte süzülür (§9).
 *
 * @param {object[]} rows
 * @param {object} cfg
 * @param {(row) => string} cfg.segmentOf - satırın segment anahtarı
 * @param {(row, text: string) => boolean} cfg.matches - arama eşleşmesi (text küçük harf)
 * @param {Record<string, (a, b) => number>} cfg.sorters - artan sırada karşılaştırıcılar
 * @param {Record<string, "asc"|"desc">} cfg.defaultDir
 * @param {string} cfg.initialSort
 * @param {(row) => string[]} [cfg.extraSegmentsOf] - satırın ek olarak sayıldığı segmentler
 * @param {string} [cfg.persistKey] - verilirse arama/segment/sıralama sessionStorage'da tutulur
 *   (listeden detay sayfasına gidip geri dönünce liste aynı görünümle açılır)
 */
export function useClientList(rows, { segmentOf, matches, sorters, defaultDir, initialSort, extraSegmentsOf, persistKey }) {
  const [persisted] = useState(() => (persistKey ? readPersistedFilters(persistKey) : null) || {});
  const [search, setSearchRaw] = useState(persisted.search || "");
  const [segment, setSegmentRaw] = useState(persisted.segment || "all");
  const [sort, setSort] = useState(persisted.sort && sorters[persisted.sort] ? persisted.sort : initialSort);
  const [dir, setDir] = useState(persisted.dir || defaultDir[initialSort] || "asc");

  useEffect(() => {
    if (persistKey) writePersistedFilters(persistKey, { search, segment, sort, dir });
  }, [persistKey, search, segment, sort, dir]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPageRaw] = useState(25);

  const text = search.trim().toLocaleLowerCase("tr");

  // Arama sonucu — segment sayıları aramaya göre güncellenir
  const searched = useMemo(() => (text ? rows.filter((r) => matches(r, text)) : rows), [rows, text, matches]);

  const counts = useMemo(() => {
    const c = { all: searched.length };
    searched.forEach((r) => {
      const keys = [segmentOf(r), ...(extraSegmentsOf ? extraSegmentsOf(r) : [])];
      keys.forEach((k) => {
        c[k] = (c[k] || 0) + 1;
      });
    });
    return c;
  }, [searched, segmentOf, extraSegmentsOf]);

  const filtered = useMemo(() => {
    const list =
      segment === "all"
        ? searched
        : searched.filter((r) => segmentOf(r) === segment || (extraSegmentsOf && extraSegmentsOf(r).includes(segment)));
    const cmp = sorters[sort];
    if (!cmp) return list;
    const sign = dir === "asc" ? 1 : -1;
    return [...list].sort((a, b) => sign * cmp(a, b));
  }, [searched, segment, segmentOf, extraSegmentsOf, sorters, sort, dir]);

  const pagedRows = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage]
  );

  const onSort = (field) => {
    if (field === sort) setDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSort(field);
      setDir(defaultDir[field] || "asc");
    }
    setPage(0);
  };

  return {
    search,
    setSearch: (v) => {
      setSearchRaw(v);
      setPage(0);
    },
    searchText: search.trim(),
    segment,
    setSegment: (v) => {
      setSegmentRaw(v);
      setPage(0);
    },
    counts,
    sort,
    dir,
    onSort,
    page,
    rowsPerPage,
    onPageChange: (e, p) => setPage(p),
    onRowsPerPageChange: (e) => {
      setRowsPerPageRaw(parseInt(e.target.value, 10));
      setPage(0);
    },
    filtered,
    pagedRows,
  };
}
